import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import {
  fetchDashboardSummary,
  fetchSimulator,
  updateSlider,
  runSensitivity,
} from './bie.apis';

import {
  fetchBieEntries,
  fetchBieLookupPack,
  fetchBieObjectiveGaps,
  fetchBieConstraints,
  saveBieEntries,
  resetBieScenarioWorkingSet,
} from './bieSimulatorApi.additions';

import {
  IBieDashboardPayload,
  IBieScenarioOption,
  IBieSimulatorPayload,
} from './bie.models';

import DashboardCards from './DashboardCards';
import BreakdownPanel from './BreakdownPanel';
import MarginBridge from './MarginBridge';
import DriverMixer from './DriverMixer';
import BieWorkbenchFilterBar from './BieWorkbenchFilterBar';
import BieExecutiveDecisionWorkbenchPanel from './BieExecutiveDecisionWorkbenchPanel';
import SensitivityTable from './SensitivityTable';
import BieSimulatorAssumptionsPanel from './BieSimulatorAssumptionsPanel';
import BieDashboardCardsPanel from './BieDashboardCardsPanel';
import ImportWizardV2 from './ImportWizardV2';
import BieSimulatorTabs from './BieSimulatorTabs';
import BieSimulatorFlowBar from './BieSimulatorFlowBar';
import { ComboRow } from '../core/coremodels';
import { fetchBieScenarioCombo } from './bieApiModern';

import SmartSimulatorResultsPanel from './SmartSimulatorResultsPanel';
import BieWhatIfObjectiveGapPanel from './BieWhatIfObjectiveGapPanel';

type State = {
  scenario: string;
  scenarios: ComboRow[];
  scenarioOptions: IBieScenarioOption[];
  loading: boolean;
  error?: string;
  success?: string;
  dashboard?: IBieDashboardPayload;
  simulator?: IBieSimulatorPayload;
  sensitivity?: any[];
  objectiveGaps: any[];
  constraints: any[];
  entries: any[];
  lookups: any;
  activeTab: string;
  importOpen: boolean;
};

class BieWorkbenchV2 extends React.Component<any, State> {
  state: State = {
    scenario: 'DET2027_BASE',
    scenarios: [],
    scenarioOptions: [],
    loading: false,
    success: '',
    objectiveGaps: [],
    constraints: [],
    entries: [],
    lookups: {},
    activeTab: 'simulator',
    importOpen: false,
  };

  async componentDidMount() {
    await this.loadCombos();
    await this.loadLookups();
    await this.load();
  }

  loadCombos = async () => {
    try {
      const scenarios = await fetchBieScenarioCombo();
      this.setState({ scenarios });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    }
  };

  loadLookups = async () => {
    try {
      const lookups = await fetchBieLookupPack();
      this.setState({ lookups });
    } catch (err: any) {
      console.warn('BIE lookup pack not available yet.', err);
    }
  };

  filters = () => ({ scc: this.state.scenario || null });

  load = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      const { scc: scenario } = this.filters();
      if (!scenario) return;

      const [dashboard, simulator, objectiveGaps, constraints, entries] = await Promise.all([
        fetchDashboardSummary(scenario),
        fetchSimulator(scenario),
        fetchBieObjectiveGaps(scenario).catch(() => []),
        fetchBieConstraints(scenario).catch(() => []),
        fetchBieEntries(scenario).catch(() => []),
      ]);

      this.setState({
        dashboard: dashboard as IBieDashboardPayload,
        simulator: simulator as IBieSimulatorPayload,
        objectiveGaps,
        constraints,
        entries,
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSliderCommit = async (drivercode: string, value: number) => {
    try {
      this.setState({ loading: true, error: undefined });
      await updateSlider(this.state.scenario, drivercode, value);
      await this.load();
    } catch (e: any) {
      this.setState({ error: e.message || String(e), loading: false });
    }
  };

  handleSensitivity = async () => {
    try {
      this.setState({ loading: true, error: undefined });
      const res: any = await runSensitivity(this.state.scenario, 'RESPARTVAL', 10);
      const payload = res.sd ? res : res.sd || res;
      this.setState({ sensitivity: payload.sd || [], activeTab: 'analysis' });
    } catch (e: any) {
      this.setState({ error: e.message || String(e) });
    } finally {
      this.setState({ loading: false });
    }
  };

  applyScenario = async () => {
    this.setState({ success: 'Scenario application workflow is not yet wired.' });
  };

  resetScenario = async () => {
    if (!this.state.scenario) return;

    this.setState({ loading: true, error: '', success: '' });

    try {
      await resetBieScenarioWorkingSet(this.state.scenario);
      this.setState({ success: 'Scenario working set reset.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  saveEntries = async (rows: any[]) => {
    await saveBieEntries(this.state.scenario, rows);
    this.setState({ success: 'Simulation entries saved.' });
    await this.load();
  };

  renderDashboardTab() {
    const { dashboard, simulator } = this.state;

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <DashboardCards cards={dashboard?.cards || simulator?.kpi} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <BieDashboardCardsPanel dashboard={dashboard} />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <BreakdownPanel title="Revenue Breakdown" rows={dashboard?.revenue || []} />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <BreakdownPanel title="Cost Breakdown" rows={dashboard?.costs || []} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <MarginBridge rows={dashboard?.bridge || []} />
        </Grid>
      </Grid>
    );
  }

  renderSimulatorTab0() {
    const { loading, simulator, dashboard } = this.state;

    return (
      <Grid container spacing={2} alignItems="stretch">
        <Grid size={{ xs: 12 }}>
          <BieSimulatorAssumptionsPanel scenarioCode={this.state.scenario} />
        </Grid>

        <Grid size={{ xs: 12, lg: 7 }}>
          <DriverMixer
            drivers={simulator?.drivers || []}
            disabled={loading}
            onChangeCommitted={this.handleSliderCommit}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <SensitivityTable rows={this.state.sensitivity || dashboard?.sensitivity || []} />
        </Grid>
      </Grid>
    );
  }

  renderSimulatorTab() {
    const { loading, simulator, dashboard, objectiveGaps, constraints } = this.state;

    return (
      <Grid container spacing={2} alignItems="stretch">
        {/* <Grid size={{ xs: 12 }}>
          <BieSimulatorAssumptionsPanel scenarioCode={this.state.scenario} />
        </Grid> */}

        <Grid size={{ xs: 12, lg: 7 }}>
          <DriverMixer
            drivers={simulator?.drivers || []}
            disabled={loading}
            onChangeCommitted={this.handleSliderCommit}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 5 }}>
          <SmartSimulatorResultsPanel
            kpi={simulator?.kpi || dashboard?.cards || {}}
            constraints={constraints || []}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <BieWhatIfObjectiveGapPanel
            rows={objectiveGaps || []}
            loading={loading}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <SensitivityTable rows={this.state.sensitivity || dashboard?.sensitivity || []} />
        </Grid>
      </Grid>
    );
  }

  renderInputsTab() {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ImportWizardV2
            scenario={this.state.scenario}
            rows={this.state.entries}
            lookups={this.state.lookups}
            loading={this.state.loading}
            onRowsChange={(entries) => this.setState({ entries })}
            onSubmitRows={this.saveEntries}
            onImported={this.load}
          />
        </Grid>
      </Grid>
    );
  }

  renderAnalysisTab() {
    const { dashboard, simulator } = this.state;

    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <BieExecutiveDecisionWorkbenchPanel
            scenarioCode={this.state.scenario}
            summary={dashboard?.cards || simulator?.kpi || {}}
            objectiveGaps={this.state.objectiveGaps}
            constraints={this.state.constraints}
            loading={this.state.loading}
            onApplyScenario={this.applyScenario}
          />
        </Grid>
      </Grid>
    );
  }

  renderAssumptionsTab() {
    return (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <BieSimulatorAssumptionsPanel
            scenarioCode={this.state.scenario}
          />
        </Grid>
      </Grid>
      
    );
  }

  renderActiveTab() {
    switch (this.state.activeTab) {
      case 'assumptions':
        return this.renderAssumptionsTab();
      case 'dashboard':
        return this.renderDashboardTab();
      case 'inputs':
        return this.renderInputsTab();
      case 'analysis':
        return this.renderAnalysisTab();
      case 'simulator':
      default:
        return this.renderSimulatorTab();
    }
  }

  render() {
    const { loading, scenario, scenarios, error, success } = this.state;

    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800}>
                BIE Simulation Workbench
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Budget Impact Engine · Scenario analytics · Driver mixer
              </Typography>
            </Box>

            <BieWorkbenchFilterBar
              scenario={scenario}
              scenarios={scenarios}
              loading={loading}
              onScenarioChange={(v: string) => this.setState({ scenario: v }, this.load)}
              onRefresh={this.load}
              onRunSensitivity={this.handleSensitivity}
              onOpenImport={() => this.setState({ activeTab: 'inputs', importOpen: true })}
            />
          </Stack>

          <BieSimulatorFlowBar
            scenario={scenario}
            activeStep={
              this.state.activeTab === 'inputs'
                ? 'INPUTS'
                : this.state.activeTab === 'simulator'
                  ? 'MIXER'
                  : this.state.activeTab === 'analysis'
                    ? 'RESULTS'
                    : 'SETUP'
            }
            loading={loading}
            onReset={this.resetScenario}
            onOpenImport={() => this.setState({ activeTab: 'inputs' })}
            onRunSensitivity={this.handleSensitivity}
            onApplyScenario={this.applyScenario}
          />

          <BieSimulatorTabs
            value={this.state.activeTab}
            onChange={(activeTab) => this.setState({ activeTab })}
          />

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          {loading && (
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Computing scenario...</Typography>
            </Paper>
          )}

          {this.renderActiveTab()}
        </Stack>

        <Dialog
          open={this.state.importOpen}
          onClose={() => this.setState({ importOpen: false })}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle>Simulation Entries & Import</DialogTitle>
          <DialogContent dividers>
            {this.renderInputsTab()}
          </DialogContent>
        </Dialog>
      </Box>
    );
  }
}

export default BieWorkbenchV2;
